import { Injectable } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ContractDTO } from "app/data/schema/Subscription/ContractDTO";
import { InitialRegistrationSettingsDTO } from "app/data/schema/Subscription/InitialRegistrationSettingsDTO";
import { IpLimitationConfigDTO } from "app/data/schema/Subscription/IpLimitationConfigDTO";
import { LanguageDTO } from "app/data/schema/Subscription/LanguageDTO";
import { LoginApiRestSettingsDTO } from "app/data/schema/Subscription/LoginApiRestSettingsDTO";
import { LoginSettings } from "app/data/schema/Subscription/LoginSettings";
import { SSOSettings } from "app/data/schema/Subscription/SSOSettings";
import { SubscriptionDTO } from "app/data/schema/Subscription/SubscriptionDTO";
import { ThemeDTO } from "app/data/schema/Subscription/ThemeDTO";
import { TimeLimitationConfigDTO } from "app/data/schema/Subscription/TimeLimitationConfigDTO";
import { UserCustomFieldDTO } from "app/data/schema/Subscription/UserCustomFieldDTO";
import { ApiCustomFieldsService } from "app/data/service/api-custom-fields.service";
import { ApiSubscriptionsService } from "app/data/service/subscription/api-subscriptions.service";
import { environment } from "environments/environment";
import { BehaviorSubject, EMPTY, Observable, Subject } from "rxjs";
import { filter, mergeMap, takeUntil, tap } from "rxjs/operators";
import { ThemeConfig } from "./theme/theme-config";
import { ThemingService } from "./theme/theming.service";

@Injectable({
    providedIn: "root"
})
export class SubscriptionService {
    private LOCAL_STORAGE_CURRENT_SUB = "current_subscription";

    private userCustomFields = new BehaviorSubject<UserCustomFieldDTO[]>([]);
    public userCustomFieldsAsync = (): Observable<UserCustomFieldDTO[]> => this.userCustomFields.asObservable();

    private subscriptions$: BehaviorSubject<SubscriptionDTO[]>;
    public subscriptionsAsync = (): Observable<SubscriptionDTO[]> => this.subscriptions$.asObservable();

    public currentSubscription$: BehaviorSubject<SubscriptionDTO>;
    public currentSubscriptionAsync = (): Observable<SubscriptionDTO> => this.currentSubscription$.asObservable();

    get currentSubscription(): SubscriptionDTO {
        return this.currentSubscription$.getValue();
    }

    // Private
    private _unsubscribeAll: Subject<any>;

    constructor(
        private _apiSubscriptionsService: ApiSubscriptionsService,
        private _themingService: ThemingService,
        private titleService: Title,
        private customFieldsService: ApiCustomFieldsService
    ) {
        this.subscriptions$ = new BehaviorSubject<SubscriptionDTO[]>([]);

        const cachedSubscription: SubscriptionDTO =
            JSON.parse(localStorage.getItem(this.LOCAL_STORAGE_CURRENT_SUB)) || (null as SubscriptionDTO);
        this.currentSubscription$ = new BehaviorSubject<SubscriptionDTO>(cachedSubscription);

        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    public initialize(): Promise<any> {
        this.subscribeToCurrentSubscriptionChanges();

        this._themingService.setCached(location.hostname);

        return this._apiSubscriptionsService
            .GetSubscriptionByHost(
                location.hostname != "localhost" ? location.hostname : environment.default_published_url
            )
            .pipe(
                takeUntil(this._unsubscribeAll),
                mergeMap((response: SubscriptionDTO) => {
                    this.subscriptions$.next([response]);
                    this.setCurrentSubscription(response);

                    return this._themingService.initialize(this.generateTheme(this.currentSubscription$.getValue()));
                })
            )
            .toPromise();
    }

    private subscribeToCurrentSubscriptionChanges(): void {
        this.currentSubscription$
            .pipe(takeUntil(this._unsubscribeAll), filter<SubscriptionDTO>(Boolean))
            .subscribe((sub: SubscriptionDTO) => {
                this.titleService.setTitle(sub.name);
                const newTheme: ThemeConfig = this.generateTheme(sub);
                this._themingService.setTheme(newTheme, sub.id);

                localStorage.setItem(this.LOCAL_STORAGE_CURRENT_SUB, JSON.stringify(sub));
            });
    }

    public setCurrentSubscription(subscription: SubscriptionDTO): void {
        this.userCustomFields.next(subscription.settings.userCustomFields ?? []);
        this.currentSubscription$.next(new SubscriptionDTO(subscription));
    }

    public ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    private generateTheme(sub: SubscriptionDTO): ThemeConfig {
        return {
            themeId: sub.id,
            mainColors: {
                primaryColor: sub.theme.primaryColor,
                accentColor: sub.theme.accentColor,
                warnColor: sub.theme.warnColor
            },
            images: {
                primaryLogoUrl: sub.theme.primaryLogoUrl,
                secondaryLogoUrl: sub.theme.secondaryLogoUrl,
                poweredByLogoUrl: sub.theme.poweredByLogoUrl,
                bannerUrl: sub.theme.bannerUrl,
                favIconUrl: sub.theme.favIconUrl ?? "/assets/icons/app-icon.png",
                adminLogoUrl: sub.theme.adminLogoUrl
            }
        } as ThemeConfig;
    }

    updateSubscriptionData(subscription: SubscriptionDTO): Observable<any> {
        return this._apiSubscriptionsService.UpdateSubscriptionData(subscription);
    }

    updateSubscriptionTheme(theme: ThemeDTO): Observable<any> {
        return this._apiSubscriptionsService.UpdateSubscriptionTheme(this.currentSubscription.id, theme);
    }

    getSubscriptionById(): Observable<SubscriptionDTO> {
        if (!this.currentSubscription?.id) return EMPTY;
        return this._apiSubscriptionsService.getById(this.currentSubscription.id);
    }

    uploadBanner(file: File): Observable<any> {
        return this._apiSubscriptionsService.UploadBanner(this.currentSubscription.id, file);
    }

    uploadBannerLogin(file: File): Observable<any> {
        return this._apiSubscriptionsService.UploadBannerLogin(this.currentSubscription.id, file);
    }

    uploadPrimaryLogo(file: File): Observable<any> {
        return this._apiSubscriptionsService.UploadPrimaryLogo(this.currentSubscription.id, file);
    }

    uploadSecundaryLogo(file: File): Observable<any> {
        return this._apiSubscriptionsService.UploadSecundaryLogo(this.currentSubscription.id, file);
    }

    uploadAdminLogo(file: File): Observable<any> {
        return this._apiSubscriptionsService.UploadAdminLogo(this.currentSubscription.id, file);
    }

    uploadPoweredByLogo(file: File): Observable<any> {
        return this._apiSubscriptionsService.UploadPoweredByLogo(this.currentSubscription.id, file);
    }

    uploadFavIcon(file: File): Observable<any> {
        return this._apiSubscriptionsService.UploadFavIcon(this.currentSubscription.id, file);
    }

    public InsertCustomField(field: UserCustomFieldDTO): Observable<UserCustomFieldDTO> {
        return this.customFieldsService.AddCustomField(field, this.currentSubscription.id).pipe(
            tap(() => {
                this._apiSubscriptionsService
                    .getById(this.currentSubscription.id)
                    .subscribe((res) => this.setCurrentSubscription(res));
            })
        );
    }

    public removeCustomField(fieldId: string): Observable<any> {
        return this.customFieldsService.RemoveCustomField(fieldId, this.currentSubscription.id).pipe(
            tap(() => {
                this._apiSubscriptionsService
                    .getById(this.currentSubscription.id)
                    .subscribe((res) => this.setCurrentSubscription(res));
            })
        );
    }

    public updateCustomField(newField: UserCustomFieldDTO): Observable<any> {
        return this.customFieldsService.UpdateCustomField(newField, this.currentSubscription.id).pipe(
            tap(() => {
                this._apiSubscriptionsService
                    .getById(this.currentSubscription.id)
                    .subscribe((res) => this.setCurrentSubscription(res));
            })
        );
    }

    public UpdateLoginSettings(loginSettings: LoginSettings): Observable<any> {
        return this._apiSubscriptionsService.UpdateLoginSettings(this.currentSubscription.id, loginSettings);
    }

    public UpdateIpLimitation(data: IpLimitationConfigDTO[]): Observable<any> {
        return this._apiSubscriptionsService.UpdateIpLimitation(this.currentSubscription.id, data);
    }

    public UpdateTimelimitation(data: TimeLimitationConfigDTO[]): Observable<any> {
        return this._apiSubscriptionsService.UpdateTimelimitation(this.currentSubscription.id, data);
    }

    public UpdateApiRestSettings(settings: LoginApiRestSettingsDTO): Observable<any> {
        return this._apiSubscriptionsService.UpdateApiRestSettings(this.currentSubscription.id, settings);
    }

    public GetLoginsApiRestSettings(): Observable<any> {
        return this._apiSubscriptionsService.GetLoginsApiRestSettings(this.currentSubscription.id);
    }

    saveSsoSetting(ssoSettings: SSOSettings, index?: number): Observable<any> {
        if (index >= 0) this.currentSubscription.loginSettings.ssoSettings[index] = ssoSettings;
        else this.currentSubscription.loginSettings.ssoSettings.push(ssoSettings);

        return this.UpdateLoginSettings(this.currentSubscription.loginSettings);
    }

    removeSsoSetting(index: number): Observable<any> {
        this.currentSubscription.loginSettings.ssoSettings.splice(index, 1);

        return this.UpdateLoginSettings(this.currentSubscription.loginSettings);
    }

    saveIpLimitation(data: IpLimitationConfigDTO, index?: number): Observable<any> {
        if (index >= 0) this.currentSubscription.loginSettings.accessLimitationConfig.ipLimitationConfig[index] = data;
        else this.currentSubscription.loginSettings.accessLimitationConfig.ipLimitationConfig.push(data);

        return this.UpdateIpLimitation(
            this.currentSubscription.loginSettings.accessLimitationConfig.ipLimitationConfig
        );
    }

    removeIpLimitation(index: number): Observable<any> {
        this.currentSubscription.loginSettings.accessLimitationConfig.ipLimitationConfig.splice(index, 1);

        return this.UpdateIpLimitation(
            this.currentSubscription.loginSettings.accessLimitationConfig.ipLimitationConfig
        );
    }

    saveTimeLimitation(data: TimeLimitationConfigDTO, index?: number): Observable<any> {
        if (index >= 0)
            this.currentSubscription.loginSettings.accessLimitationConfig.timeLimitationConfig[index] = data;
        else this.currentSubscription.loginSettings.accessLimitationConfig.timeLimitationConfig.push(data);

        return this.UpdateTimelimitation(
            this.currentSubscription.loginSettings.accessLimitationConfig.timeLimitationConfig
        );
    }

    removeTimeLimitation(index: number): Observable<any> {
        this.currentSubscription.loginSettings.accessLimitationConfig.timeLimitationConfig.splice(index, 1);

        return this.UpdateTimelimitation(
            this.currentSubscription.loginSettings.accessLimitationConfig.timeLimitationConfig
        );
    }

    requestDataReview(): Observable<any> {
        return this._apiSubscriptionsService.RequesDataReview(this.currentSubscription.id);
    }

    updateSubscrictionContract(subscriptionId: string, contract: ContractDTO): Observable<any> {
        return this._apiSubscriptionsService.UpdateSubscriptionContract(subscriptionId, contract);
    }

    GetPrivacyPolicyTemplate(): Observable<string> {
        return this._apiSubscriptionsService.GetPrivacyPolicyTemplate(this.currentSubscription.id);
    }

    SetPrivacyPolicyTemplate(template: string): Observable<string> {
        return this._apiSubscriptionsService.SetPrivacyPolicyTemplate(this.currentSubscription.id, template);
    }

    GetTermsOfUseTemplate(): Observable<string> {
        return this._apiSubscriptionsService.GetTermsOfUseTemplate(this.currentSubscription.id);
    }

    SetTermsOfUseTemplate(template: string): Observable<string> {
        return this._apiSubscriptionsService.SetTermsOfUseTemplate(this.currentSubscription.id, template);
    }

    GetTermsOfPurchaseTemplate(): Observable<string> {
        return this._apiSubscriptionsService.GetTermsOfPurchaseTemplate(this.currentSubscription.id);
    }

    SetTermsOfPurchaseTemplate(template: string): Observable<string> {
        return this._apiSubscriptionsService.SetTermsOfPurchaseTemplate(this.currentSubscription.id, template);
    }

    updateInitialRegistrationSettings(settings: InitialRegistrationSettingsDTO): Observable<any> {
        return this._apiSubscriptionsService.updateInitialRegistrationSettings(this.currentSubscription.id, settings);
    }

    updateDefaultLanguage(language: LanguageDTO): Observable<any> {
        return this._apiSubscriptionsService.updateDefaultLanguage(this.currentSubscription.id, language);
    }
}
